<template>
  <div class="system-setting">
    <div class="page-header">
      <h2 class="page-title">系统设置</h2>
      <p class="page-desc">配置小程序、微信支付等系统参数</p>
    </div>

    <div class="setting-container">
      <!-- 小程序配置 -->
      <el-card class="setting-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <el-icon>
                <Phone />
              </el-icon>
              <span>小程序配置</span>
            </div>
            <div class="card-header-actions">
              <el-button type="primary" link @click="openUploadCodeDialog" size="small">
                <el-icon>
                  <Upload />
                </el-icon> 上传代码
              </el-button>
              <el-button type="primary" link @click="openMiniProgramDialog" size="small">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
            </div>
          </div>
        </template>
        <div class="config-content">
          <div class="config-item">
            <span class="config-label">AppID</span>
            <span class="config-value">{{ miniProgramConfig.appid || '未配置' }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">AppSecret</span>
            <span class="config-value">
              <span v-if="miniProgramConfig.appsecret">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">上传密钥</span>
            <span class="config-value">{{ miniProgramConfig.upload_key ? '已配置' : '未配置' }}</span>
          </div>
        </div>
      </el-card>

      <!-- 微信支付配置 -->
      <el-card class="setting-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <el-icon>
                <Wallet />
              </el-icon>
              <span>微信支付配置</span>
            </div>
            <div class="card-header-actions">
              <el-button type="primary" link @click="goToPayTest" size="small">
                <el-icon>
                  <CreditCard />
                </el-icon> 测试支付
              </el-button>
              <el-button type="primary" link @click="openPayDialog" size="small">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
            </div>
          </div>
        </template>
        <div class="config-content">
          <div class="config-item">
            <span class="config-label">商户号</span>
            <span class="config-value">{{ payConfig.mchid || '未配置' }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">API密钥</span>
            <span class="config-value">
              <span v-if="payConfig.api_key">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">APIv3密钥</span>
            <span class="config-value">
              <span v-if="payConfig.api_v3_key">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">商户证书</span>
            <span class="config-value">{{ payConfig.cert_files && Object.keys(payConfig.cert_files).length > 0 ? '已配置' :
              '未配置'
            }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">支付模式</span>
            <span class="config-value">{{ payConfig.pay_mode === 'production' ? '生产模式' : '沙箱模式' }}</span>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 小程序配置弹窗 -->
    <el-dialog v-model="miniProgramDialogVisible" title="编辑小程序配置" width="550px" destroy-on-close>
      <el-form :model="miniProgramForm" label-width="110px">
        <el-form-item label="AppID" required>
          <el-input v-model="miniProgramForm.appid" placeholder="请输入小程序AppID" />
        </el-form-item>
        <el-form-item label="AppSecret" required>
          <el-input v-model="miniProgramForm.appsecret" type="password" placeholder="请输入小程序AppSecret" show-password />
          <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
        </el-form-item>
        <el-form-item label="上传密钥">
          <el-input v-model="miniProgramForm.upload_key" type="textarea" :rows="3" placeholder="请输入小程序上传密钥（可选）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="miniProgramDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveMiniProgramConfig" :loading="saving">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 微信支付配置弹窗 -->
    <el-dialog v-model="payDialogVisible" title="编辑微信支付配置" width="650px" destroy-on-close>
      <el-form :model="payForm" label-width="110px">
        <el-form-item label="商户号" required>
          <el-input v-model="payForm.mchid" placeholder="请输入微信支付商户号" />
        </el-form-item>
        <el-form-item label="API密钥" required>
          <el-input v-model="payForm.api_key" type="password" placeholder="请输入API密钥（32位）" show-password />
          <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
        </el-form-item>
        <el-form-item label="APIv3密钥">
          <el-input v-model="payForm.api_v3_key" type="password" placeholder="请输入APIv3密钥（选填）" show-password />
        </el-form-item>
        <el-form-item label="商户证书">
          <el-upload :file-list="certFiles" action="#" :limit="2" accept=".pem" :auto-upload="false"
            :on-change="handleCertChange" :on-remove="handleCertRemove">
            <el-button type="primary" link>
              <el-icon>
                <Upload />
              </el-icon> 上传证书
            </el-button>
            <template #tip>
              <div class="upload-tip">支持 .pem 格式，最多上传2个文件（apiclient_cert.pem 和 apiclient_key.pem）</div>
            </template>
          </el-upload>
          <div v-if="Object.keys(certData).length > 0" class="cert-list">
            <div v-for="(url, name) in certData" :key="name" class="cert-item">
              <span>{{ name }}</span>
              <el-tag size="small" type="success">已上传</el-tag>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="支付模式">
          <el-radio-group v-model="payForm.pay_mode">
            <el-radio label="sandbox">沙箱模式（测试）</el-radio>
            <el-radio label="production">生产模式（正式）</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="payDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePayConfig" :loading="saving">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 上传代码弹窗 -->
    <el-dialog v-model="uploadCodeDialogVisible" title="上传小程序代码" width="650px" destroy-on-close @close="resetUploadForm">
      <el-form :model="uploadForm" label-width="120px">
        <el-form-item label="小程序AppID" required>
          <el-input v-model="uploadForm.appid" disabled placeholder="请先配置小程序AppID" />
          <div class="form-tip">从小程序配置中自动获取</div>
        </el-form-item>
        <el-form-item label="上传私钥" required>
          <el-input v-model="uploadForm.privateKeyContent" type="textarea" :rows="8" disabled placeholder="请先配置上传密钥" />
          <div class="form-tip">从小程序配置中自动获取（upload_key）</div>
        </el-form-item>
        <el-form-item label="API域名" required>
          <el-input v-model="uploadForm.api_url" disabled />
          <div class="form-tip">自动使用系统配置的API域名</div>
        </el-form-item>
        <el-form-item label="商家ID" required>
          <el-input-number v-model="uploadForm.default_store_id" :disabled="true" :min="1" />
          <div class="form-tip">自动使用当前商家ID</div>
        </el-form-item>
      </el-form>
      
      <!-- 上传进度 -->
      <div v-if="uploading" class="upload-progress">
        <el-progress :percentage="uploadProgress" :status="uploadProgressStatus" />
        <div class="upload-log">
          <div v-for="(log, index) in uploadLogs" :key="index" class="log-line">{{ log }}</div>
        </div>
      </div>
      
      <template #footer>
        <el-button @click="uploadCodeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitUploadCode" :loading="uploading" :disabled="uploading">
          {{ uploading ? '上传中...' : '开始上传' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadCertToLocal } from '@/utils/uploadUtils'
import { CreditCard, Phone, Wallet, Edit, Upload } from '@element-plus/icons-vue'

export default {
  name: 'SystemSettingPage',
  components: { CreditCard, Phone, Wallet, Edit, Upload },
  setup() {
    const router = useRouter()

    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id)
    const agentId = ref(userInfo.agent_id || 0)

    const saving = ref(false)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const uploadProgressStatus = ref('')
    const uploadLogs = ref([])

    // 配置数据
    const miniProgramConfig = ref({})
    const payConfig = ref({})

    // 弹窗状态
    const miniProgramDialogVisible = ref(false)
    const payDialogVisible = ref(false)
    const uploadCodeDialogVisible = ref(false)

    // 表单数据
    const miniProgramForm = reactive({
      appid: '',
      appsecret: '',
      upload_key: ''
    })

    const payForm = reactive({
      mchid: '',
      api_key: '',
      api_v3_key: '',
      pay_mode: 'sandbox'
    })

    // 上传表单（只读，自动填充）
    const uploadForm = reactive({
      appid: '',
      privateKeyContent: '',
      api_url: config.BASE_URL,
      default_store_id: 2
    })

    // 证书文件
    const certFiles = ref([])
    const certData = ref({})

    // 获取配置
    const fetchConfig = async () => {
      const loading = ElLoading.service({ text: '加载中...' })
      try {
        // 获取小程序配置
        const miniRes = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'miniprogram' }
        })
        if (miniRes.data.code === 200 && miniRes.data.data) {
          miniProgramConfig.value = miniRes.data.data
          // 自动填充到上传表单
          uploadForm.appid = miniProgramConfig.value.appid || ''
          uploadForm.privateKeyContent = miniProgramConfig.value.upload_key || ''
        }

        // 获取支付配置
        const payRes = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'wechat_pay' }
        })
        if (payRes.data.code === 200 && payRes.data.data) {
          payConfig.value = payRes.data.data
          if (payConfig.value.cert_files) {
            certData.value = payConfig.value.cert_files
          }
        }
        
        // 自动填充门店 ID
        uploadForm.default_store_id = storeId.value
        // 设置 API 域名
        uploadForm.api_url = config.BASE_URL
      } catch (error) {
        console.error('获取配置失败:', error)
        ElMessage.error('获取配置失败')
      } finally {
        loading.close()
      }
    }

    // 重置上传表单
    const resetUploadForm = () => {
      uploadProgress.value = 0
      uploadProgressStatus.value = ''
      uploadLogs.value = []
    }

    // 打开上传代码弹窗
    const openUploadCodeDialog = () => {
      // 重新填充最新配置
      uploadForm.appid = miniProgramConfig.value.appid || ''
      uploadForm.privateKeyContent = miniProgramConfig.value.upload_key || ''
      uploadForm.default_store_id = storeId.value
      uploadForm.api_url = config.BASE_URL
      uploadLogs.value = []
      uploadProgress.value = 0
      uploadProgressStatus.value = ''
      
      // 检查必要配置
      if (!uploadForm.appid) {
        ElMessage.warning('请先配置小程序 AppID')
        return
      }
      if (!uploadForm.privateKeyContent) {
        ElMessage.warning('请先配置小程序上传密钥')
        return
      }
      
      uploadCodeDialogVisible.value = true
    }

    // 提交上传代码
    const submitUploadCode = async () => {
      // 验证表单
      if (!uploadForm.appid) {
        ElMessage.warning('请先配置小程序AppID')
        return
      }
      if (!uploadForm.privateKeyContent) {
        ElMessage.warning('请先配置小程序上传密钥')
        return
      }
      if (!uploadForm.api_url) {
        ElMessage.warning('API域名未配置')
        return
      }
      if (!uploadForm.default_store_id) {
        ElMessage.warning('商家ID未配置')
        return
      }

      uploading.value = true
      uploadProgress.value = 10
      uploadProgressStatus.value = ''
      uploadLogs.value = ['开始上传小程序代码...']

      try {
        // 调用上传接口（固定地址）
        const response = await axios.post(
          'https://db.juzhendi.com/auth/minidb/group_buy_mini/upload.php',
          {
            appid: uploadForm.appid,
            privateKeyContent: uploadForm.privateKeyContent,
            api_url: uploadForm.api_url,
            default_store_id: String(uploadForm.default_store_id)
          },
          {
            timeout: 120000, // 120秒超时
            onUploadProgress: (progressEvent) => {
              // 模拟进度
              const percent = Math.min(90, 10 + Math.floor((progressEvent.loaded || 0) / (progressEvent.total || 1) * 80))
              uploadProgress.value = percent
            }
          }
        )

        uploadProgress.value = 100
        
        // 处理响应
        if (response.data.status === 'success') {
          uploadProgressStatus.value = 'success'
          uploadLogs.value.push('✅ 上传成功！')
          if (response.data.detail && Array.isArray(response.data.detail)) {
            response.data.detail.forEach(line => {
              if (line && line.trim()) {
                uploadLogs.value.push(line)
              }
            })
          }
          ElMessage.success('小程序代码上传成功')
        } else {
          uploadProgressStatus.value = 'exception'
          uploadLogs.value.push(`❌ 上传失败: ${response.data.message}`)
          ElMessage.error(response.data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        uploadProgressStatus.value = 'exception'
        uploadLogs.value.push(`❌ 请求失败: ${error.message}`)
        
        if (error.response && error.response.data) {
          uploadLogs.value.push(`错误详情: ${JSON.stringify(error.response.data)}`)
        }
        ElMessage.error('上传失败，请检查网络或稍后重试')
      } finally {
        uploading.value = false
      }
    }

    // 打开小程序配置弹窗
    const openMiniProgramDialog = () => {
      Object.assign(miniProgramForm, {
        appid: miniProgramConfig.value.appid || '',
        appsecret: miniProgramConfig.value.appsecret || '',
        upload_key: miniProgramConfig.value.upload_key || ''
      })
      miniProgramDialogVisible.value = true
    }

    // 保存小程序配置
    const saveMiniProgramConfig = async () => {
      if (!miniProgramForm.appid) {
        ElMessage.warning('请填写AppID')
        return
      }

      saving.value = true
      const loading = ElLoading.service({ text: '保存中...' })

      try {
        const saveData = {
          appid: miniProgramForm.appid,
          upload_key: miniProgramForm.upload_key
        }

        if (miniProgramForm.appsecret) {
          saveData.appsecret = miniProgramForm.appsecret
        } else if (miniProgramConfig.value.appsecret) {
          saveData.appsecret = miniProgramConfig.value.appsecret
        } else {
          saveData.appsecret = ''
        }

        const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
          action: 'save',
          agent_id: agentId.value,
          store_id: storeId.value,
          config_key: 'miniprogram',
          config_value: JSON.stringify(saveData),
          config_group: 'miniprogram',
          description: '小程序配置'
        })

        if (response.data.code === 200) {
          ElMessage.success('保存成功')
          miniProgramConfig.value = saveData
          miniProgramDialogVisible.value = false
          fetchConfig()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
        loading.close()
      }
    }

    // 打开支付配置弹窗
    const openPayDialog = () => {
      Object.assign(payForm, {
        mchid: payConfig.value.mchid || '',
        api_key: payConfig.value.api_key || '',
        api_v3_key: payConfig.value.api_v3_key || '',
        pay_mode: payConfig.value.pay_mode || 'sandbox'
      })
      if (payConfig.value.cert_files) {
        certData.value = { ...payConfig.value.cert_files }
        certFiles.value = Object.keys(certData.value).map((name, index) => ({
          uid: Date.now() + index,
          name: name,
          url: certData.value[name]
        }))
      } else {
        certFiles.value = []
        certData.value = {}
      }
      payDialogVisible.value = true
    }

    // 上传证书
    const handleCertChange = async (file, fileList) => {
      if (!file.raw) return

      if (!file.raw.name.endsWith('.pem')) {
        ElMessage.error('请上传 .pem 格式的证书文件')
        return false
      }

      if (file.raw.size > 2 * 1024 * 1024) {
        ElMessage.error('证书文件大小不能超过2MB')
        return false
      }

      try {
        const signature = await getUploadSignature('cert')
        const result = await uploadCertToLocal(file.raw, signature)
        certData.value[file.raw.name] = result.url
        certFiles.value = fileList
        ElMessage.success(`${file.raw.name} 上传成功`)
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error(error.message || '上传失败')
      }
    }

    const handleCertRemove = (file) => {
      delete certData.value[file.name]
    }

    // 保存支付配置
    const savePayConfig = async () => {
      if (!payForm.mchid) {
        ElMessage.warning('请填写商户号')
        return
      }

      saving.value = true
      const loading = ElLoading.service({ text: '保存中...' })

      try {
        const configValue = {
          mchid: payForm.mchid,
          pay_mode: payForm.pay_mode,
          cert_files: certData.value
        }

        if (payForm.api_key) {
          configValue.api_key = payForm.api_key
        } else if (payConfig.value.api_key) {
          configValue.api_key = payConfig.value.api_key
        } else {
          configValue.api_key = ''
        }

        if (payForm.api_v3_key) {
          configValue.api_v3_key = payForm.api_v3_key
        } else if (payConfig.value.api_v3_key) {
          configValue.api_v3_key = payConfig.value.api_v3_key
        } else {
          configValue.api_v3_key = ''
        }

        const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
          action: 'save',
          agent_id: agentId.value,
          store_id: storeId.value,
          config_key: 'wechat_pay',
          config_value: JSON.stringify(configValue),
          config_group: 'payment',
          description: '微信支付配置'
        })

        if (response.data.code === 200) {
          ElMessage.success('保存成功')
          payConfig.value = configValue
          payDialogVisible.value = false
          fetchConfig()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
        loading.close()
      }
    }

    const goToPayTest = () => {
      router.push('/pay-test')
    }

    onMounted(() => {
      fetchConfig()
    })

    return {
      saving,
      uploading,
      uploadProgress,
      uploadProgressStatus,
      uploadLogs,
      miniProgramConfig,
      payConfig,
      miniProgramDialogVisible,
      payDialogVisible,
      uploadCodeDialogVisible,
      miniProgramForm,
      payForm,
      uploadForm,
      certFiles,
      certData,
      openMiniProgramDialog,
      saveMiniProgramConfig,
      openPayDialog,
      savePayConfig,
      openUploadCodeDialog,
      submitUploadCode,
      resetUploadForm,
      handleCertChange,
      goToPayTest,
      handleCertRemove
    }
  }
}
</script>

<style scoped lang="scss">
.system-setting {
  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .setting-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
    gap: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .setting-card {
    border-radius: 16px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .card-header-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;

        .el-icon {
          font-size: 18px;
          color: #409eff;
        }
      }

      .card-header-actions {
        display: flex;
        gap: 12px;
      }
    }

    .config-content {
      .config-item {
        display: flex;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .config-label {
          width: 100px;
          color: #8c8c8c;
          font-size: 14px;
        }

        .config-value {
          flex: 1;
          color: #333;
          font-size: 14px;
          word-break: break-all;
        }
      }
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 8px;
  }

  .form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
  }

  .cert-list {
    margin-top: 12px;

    .cert-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: #f5f7fa;
      border-radius: 6px;
      margin-bottom: 8px;
      font-size: 13px;

      span {
        color: #333;
        font-family: monospace;
      }
    }
  }

  .upload-progress {
    margin-top: 20px;
    
    .upload-log {
      margin-top: 12px;
      max-height: 300px;
      overflow-y: auto;
      background: #1e1e1e;
      border-radius: 8px;
      padding: 12px;
      
      .log-line {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 12px;
        color: #d4d4d4;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-all;
      }
    }
  }
}
</style>